import * as Actions from '../actions/flatFeeMilestoneActions';
import FlatFeeMilestone from '../../entities/flatFeeMilestone';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type FlatFeeMilestoneAction = { 
    type: string; 
    flatFeeMilestone: FlatFeeMilestone | null; 
    flatFeeMilestoneId: string | FlatFeeMilestone; 
    partial: boolean; 
    lastFetch: number; 
    flatFeeMilestones: FlatFeeMilestone[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<FlatFeeMilestone> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {FlatFeeMilestone[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<FlatFeeMilestone> | null, firmId: string, userId: string, updates?: FlatFeeMilestone[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'flatFeeMilestone', 'flatFeeMilestones', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<FlatFeeMilestone>} data
 * @param {FlatFeeMilestone[]} [updates]
 * @returns {ArrayMap<FlatFeeMilestone>}
 */
function updateStorage(data: ArrayMap<FlatFeeMilestone>, updates?: FlatFeeMilestone[]): ArrayMap<FlatFeeMilestone> {
	if (logging) console.debug('⮂ flatFeeMilestoneReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {FlatFeeMilestone[]} [updates]
 * @returns {ArrayMap<FlatFeeMilestone>}
 */
function updateStorageNoReturn(updates?: FlatFeeMilestone[]): void {
	if (logging) console.debug('⮂ flatFeeMilestoneReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {FlatFeeMilestone[]} [data]
 * @returns {ArrayMap<FlatFeeMilestone>}
 */
function createState(data?: FlatFeeMilestone[] | ArrayMap<FlatFeeMilestone>): ArrayMap<FlatFeeMilestone> {
	if (!data) return ArrayMapRegistry.clone<FlatFeeMilestone>('flatFeeMilestone', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<FlatFeeMilestone>('flatFeeMilestone', data.map(e => e));
    else return ArrayMapRegistry.clone<FlatFeeMilestone>('flatFeeMilestone', data);
}

function flatFeeMilestones(state: ArrayMap<FlatFeeMilestone>, action: FlatFeeMilestoneAction): ArrayMap<FlatFeeMilestone> {
	if (!state) state = createState();

	let st: ArrayMap<FlatFeeMilestone>;

	switch (action.type) {
		// action.flatFeeMilestone - the new value of flatFeeMilestone with id action.id
		// action.flatFeeMilestoneId - id of the target, used if new value is null
		case Actions.FLATFEEMILESTONE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ flatFeeMilestoneReducer: FETCH_ONE_SUCCESS', action);

			if (action.flatFeeMilestone === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.flatFeeMilestoneId)) {
				st = state.clone();
				st.set(action.flatFeeMilestone);
				// state.clear();

				updateStorageNoReturn([action.flatFeeMilestone]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.flatFeeMilestones
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.flatFeeMilestones
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.flatFeeMilestones
			 !partial, !stateEmpty, fetchEmpty	-> D return flatFeeMilestones in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge flatFeeMilestones in state newer than those in action.flatFeeMilestones
		 */
		case Actions.FLATFEEMILESTONE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.flatFeeMilestones.length === 0;
			const fetched = action.flatFeeMilestones.map(flatFeeMilestone => new FlatFeeMilestone({...flatFeeMilestone, persisted: true}));

			if (logging) console.debug(`⮂ flatFeeMilestoneReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} flatFeeMilestones with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ flatFeeMilestoneReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ flatFeeMilestoneReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<FlatFeeMilestone> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: FlatFeeMilestone[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(flatFeeMilestone => {
					const current = st.get(flatFeeMilestone.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && flatFeeMilestone.status === 'D') {
							// Remove it from state
							toUpdate.push(flatFeeMilestone);
							st.delete(flatFeeMilestone);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (flatFeeMilestone.addedOn > current.addedOn) {
								toUpdate.push(flatFeeMilestone);
								st.set(flatFeeMilestone);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (flatFeeMilestone.status !== 'D') {
							toUpdate.push(flatFeeMilestone);
							st.set(flatFeeMilestone);
						}
						else if (logging) console.debug('⮂ flatFeeMilestoneReducer: partialMerge: skipping deleted flatFeeMilestone not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ flatFeeMilestoneReducer: empty: flatFeeMilestones', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ flatFeeMilestoneReducer: fullMerge: flatFeeMilestones', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localFlatFeeMilestone => {
                    // If this flatFeeMilestone has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localFlatFeeMilestone.persisted) {
                        const fetchedFlatFeeMilestone = st.get(localFlatFeeMilestone.id);
                        if (!fetchedFlatFeeMilestone || localFlatFeeMilestone.addedOn > fetchedFlatFeeMilestone.addedOn) st.set(localFlatFeeMilestone);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.FLATFEEMILESTONE_FETCH_ONE_FAILURE:
			console.error('FLATFEEMILESTONE_FETCH_ONE_FAILURE', action.flatFeeMilestoneId, action.err);

			// No update to storage required
			return state;

		case Actions.FLATFEEMILESTONE_FETCH_FAILURE:
			console.error('FLATFEEMILESTONE_FETCH_FAILURE', action.flatFeeMilestoneId, action.err);

			// No update to storage required
			return state;

		case Actions.FLATFEEMILESTONE_RESTORE: {
		    let flatFeeMilestone = new FlatFeeMilestone({...action.flatFeeMilestone});
            st = state.clone();

            const existing = st.get(flatFeeMilestone.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (flatFeeMilestone.addedOn >= existing.addedOn) {
                    st.set(flatFeeMilestone);
                    return updateStorage(st, [flatFeeMilestone]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (flatFeeMilestone.status === 'D') return state;

                st.set(flatFeeMilestone);
                return updateStorage(st, [flatFeeMilestone]);
            }
        }
            
		case Actions.FLATFEEMILESTONE_SAVE: {
		    // console.debug('⮂ FLATFEEMILESTONE_SAVE reducer', action.flatFeeMilestone?.id);
		    
			// clone the flatFeeMilestone
			let flatFeeMilestone = new FlatFeeMilestone({...action.flatFeeMilestone});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new flatFeeMilestone in the state
			// If this is a create, add to the queue
			const _flatFeeMilestone = st.get(flatFeeMilestone.id);

			if (!_flatFeeMilestone) st.set(flatFeeMilestone);
			else {
				if (flatFeeMilestone.addedOn >= _flatFeeMilestone.addedOn) st.set(flatFeeMilestone);
			}

			return updateStorage(st, [flatFeeMilestone]);
		}

		case Actions.FLATFEEMILESTONE_SAVE_MANY: {
		    if (logging) console.debug('⮂ FLATFEEMILESTONE_SAVE_MANY reducer', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));
		    
			// clone the flatFeeMilestones
			let toProcess = action.flatFeeMilestones.map(flatFeeMilestone => new FlatFeeMilestone(flatFeeMilestone));

			st = state.clone();
			// state.clear();

			const updates: FlatFeeMilestone[] = [];

			toProcess.forEach(flatFeeMilestone => {
				// Add or update the new flatFeeMilestone in the state
				// If this is a create, add to the queue
				const existing = st.get(flatFeeMilestone.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (flatFeeMilestone.status !== 'D') {
						st.push(flatFeeMilestone);
						updates.push(flatFeeMilestone);
					}
					else if (logging) console.debug('⮂ FLATFEEMILESTONE_SAVE_MANY: skipping deleted flatFeeMilestone not in state');
				}
				else {
					if (flatFeeMilestone.addedOn >= existing.addedOn) {
						st.set(flatFeeMilestone);
						updates.push(flatFeeMilestone);
					}
					else if (logging) console.debug('⮂ Skipping, existing flatFeeMilestone had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.FLATFEEMILESTONE_REMOVE: {
			// clone the flatFeeMilestone
			const flatFeeMilestone = new FlatFeeMilestone({...action.flatFeeMilestone});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(flatFeeMilestone);

			return updateStorage(st, [flatFeeMilestone]);
		}

		case Actions.FLATFEEMILESTONE_RESTORE_FAILURE:
		case Actions.FLATFEEMILESTONE_SAVE_FAILURE: {
		    console.warn('FLATFEEMILESTONE_SAVE_FAILURE reducer', action.flatFeeMilestone?.id);
		    if (!action.flatFeeMilestone?.id) {
				console.warn('FLATFEEMILESTONE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const flatFeeMilestone = ensure(FlatFeeMilestone, action.flatFeeMilestone);
			if (!flatFeeMilestone) {
				console.error('Could not find flatFeeMilestone after call to save');
				return state;
			}

			st = state.clone();

			const _flatFeeMilestoneRef = state.get(flatFeeMilestone.id);

			if (_flatFeeMilestoneRef && (_flatFeeMilestoneRef.addedOn === flatFeeMilestone.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _flatFeeMilestone = new FlatFeeMilestone({..._flatFeeMilestoneRef});
				_flatFeeMilestone.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_flatFeeMilestone.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('flatFeeMilestone', _flatFeeMilestone).then(r => {
						if (r.isErr()) console.error(`⮂ flatFeeMilestoneReducer: failed to save flatFeeMilestone with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ flatFeeMilestoneReducer: saved flatFeeMilestone with fatalError info');
					}).catch(err => console.error(`⮂ flatFeeMilestoneReducer: failed to save flatFeeMilestone with fatalError info: ${err.message}`));

				st.set(_flatFeeMilestone);
			}

			return updateStorage(st);
		}

		case Actions.FLATFEEMILESTONE_SAVE_MANY_FAILURE: {
		    console.warn('FLATFEEMILESTONE_SAVE_MANY_FAILURE reducer', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.FLATFEEMILESTONE_RESTORE_SUCCESS:
		case Actions.FLATFEEMILESTONE_SAVE_SUCCESS: {
			if (!action.flatFeeMilestone?.id) {
				console.warn('FLATFEEMILESTONE_SAVE_SUCCESS reducer');
                return state;
			}

			const flatFeeMilestoneRef = state.get(action.flatFeeMilestone?.id);
			if (!flatFeeMilestoneRef) {
				console.warn('Could not find flatFeeMilestone after call to save');
                return state;
			}

			st = state.clone();

			if (action.flatFeeMilestone.addedOn >= flatFeeMilestoneRef.addedOn) {
				st.set(action.flatFeeMilestone);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const flatFeeMilestone = new FlatFeeMilestone({...flatFeeMilestoneRef, persisted: true});
				st.set(flatFeeMilestone);
			}

			return updateStorage(st, [action.flatFeeMilestone]);
		}

		case Actions.FLATFEEMILESTONE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ FLATFEEMILESTONE_SAVE_MANY_SUCCESS reducer', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const flatFeeMilestones: FlatFeeMilestone[] = [];

			action.flatFeeMilestones.forEach(e => {
				const flatFeeMilestoneRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!flatFeeMilestoneRef) console.warn('Could not find flatFeeMilestone after call to save');
				else if (e.addedOn >= flatFeeMilestoneRef.addedOn) {
				    st.set(e);
				    flatFeeMilestones.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const flatFeeMilestone = new FlatFeeMilestone({...flatFeeMilestoneRef, persisted: true});
					st.set(flatFeeMilestone);
					flatFeeMilestones.push(flatFeeMilestone);
				}
			});

			return updateStorage(st, flatFeeMilestones);
		}

		case Actions.FLATFEEMILESTONE_REMOVE_FAILURE: {
		    console.warn('FLATFEEMILESTONE_REMOVE_FAILURE action', action.flatFeeMilestone?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.FLATFEEMILESTONE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.FLATFEEMILESTONE_RESTORE_MANY: {
			if (logging) console.debug('⮂ FLATFEEMILESTONE_RESTORE_MANY reducer', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));

			const toProcess = action.flatFeeMilestones.map(flatFeeMilestone => new FlatFeeMilestone({...flatFeeMilestone}));
			st = state.clone();

			const updates: FlatFeeMilestone[] = [];

			toProcess.forEach(flatFeeMilestone => {
				const existing = st.get(flatFeeMilestone.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (flatFeeMilestone.addedOn >= existing.addedOn) {
						st.set(flatFeeMilestone);
						updates.push(flatFeeMilestone);
					}
					else if (logging) console.debug('⮂ FLATFEEMILESTONE_RESTORE_MANY: skipping older version of flatFeeMilestone');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (flatFeeMilestone.status !== 'D') {
						st.set(flatFeeMilestone);
						updates.push(flatFeeMilestone);
					}
					else if (logging) console.debug('⮂ FLATFEEMILESTONE_RESTORE_MANY: skipping deleted flatFeeMilestone not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.FLATFEEMILESTONE_REMOVE_MANY: {
			if (logging) console.debug('⮂ FLATFEEMILESTONE_REMOVE_MANY reducer', action.flatFeeMilestones.map(flatFeeMilestone => flatFeeMilestone.id).join(','));

			const toProcess = action.flatFeeMilestones.map(flatFeeMilestone => new FlatFeeMilestone({...flatFeeMilestone}));
			st = state.clone();

			toProcess.forEach(flatFeeMilestone => {
				st.delete(flatFeeMilestone);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default flatFeeMilestones;